import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

interface FwFieldObject {
    value: any,
    placeholder: string,
    name: string,
    disabled: boolean,
    verified: boolean,
    focused: boolean;
    maxlength: string | number | null;
    togglePassword: boolean;
    password: boolean;
    clear: boolean;
    clearable: boolean;
    errors: Array<Object>;
    hint: string;
}

@Component
export default class FwFieldMixin extends Vue {
    localValue: any;

    typeFilters: { [key: string]: Function } = {
        number: (value: string) => `${value}`.replace(/\D/g, ''),
    };

    constructor() {
        super();
        this.localValue = this.value;
    }

    FwField: FwFieldObject = {
        value: null,
        placeholder: '',
        name: '',
        disabled: false,
        verified: false,
        focused: false,
        maxlength: null,
        togglePassword: false,
        password: false,
        clear: false,
        clearable: false,
        errors: [],
        hint: '',
    };

    /**
     * Value to be able to use v-model directive.
     */
    @Prop() readonly value!: any;

    /**
     * Type for input field.
     */
    @Prop(String) readonly type!: string;

    /**
     * Placeholder for the field.
     */
    @Prop(String) readonly placeholder!: string;

    /**
     * Name of the field.
     */
    @Prop(String) readonly name!: string;

    /**
     * Max length for the input value. (Does not filter value, only shows a counter)
     */
    @Prop([String, Number]) readonly maxlength!: string | number;

    /**
     * Boolean prop to make input disabled.
     */
    @Prop(Boolean) readonly disabled!: boolean;

    /**
     * Boolean prop to make input clearable.
     */
    @Prop(Boolean) readonly clearable!: boolean;

    /**
     * Boolean prop to show a verified icon.
     */
    @Prop(Boolean) readonly verified!: boolean;

    /**
     * Array of errors for the input.
     */
    @Prop(Array) readonly errors!: Array<Object>;

    /**
     * Hint for the input.
     */
    @Prop(String) readonly hint!: string;

    get model(): any {
        return this.localValue;
    }

    set model(val) {
        if (this.type && this.typeFilters[this.type]) {
            this.localValue = null;
            this.localValue = this.typeFilters[this.type](val);
        } else {
            this.localValue = val;
        }
    }

    get clear(): boolean {
        return this.FwField.clear;
    }

    @Watch('model')
    modelHandler(): void {
        this.setFieldValue();
    }

    @Watch('value')
    valueHandler(val: any): void {
        this.localValue = val;
    }

    @Watch('localValue')
    inputHandler(val: any): void {
        this.$emit('input', val);
    }

    @Watch('placeholder')
    placeholderHandler(): void {
        this.setPlaceholder();
    }

    @Watch('name')
    nameHandler(): void {
        this.setName();
    }

    @Watch('disabled')
    disabledHandler(): void {
        this.setDisabled();
    }

    @Watch('clearable')
    clearableHandler(): void {
        this.setClearable();
    }

    @Watch('verified')
    verifiedHandler(): void {
        this.setVerified();
    }

    @Watch('maxlength')
    maxLengthHandler(): void {
        this.setMaxlength();
    }

    @Watch('errors')
    errorsHandler(): void {
        this.setErrors();
    }

    @Watch('hint')
    hintHandler(): void {
        this.setHint();
    }

    @Watch('clear')
    clearHandler(val: any): void {
        if (val) {
            this.clearField();
        }
    }

    setFieldValue(): void {
        this.FwField.value = this.model;
    }

    setPlaceholder(): void {
        this.FwField.placeholder = this.placeholder;
    }

    setName(): void {
        this.FwField.name = this.name;
    }

    setDisabled(): void {
        this.FwField.disabled = Boolean(this.disabled);
    }

    setClearable(): void {
        this.FwField.clearable = Boolean(this.clearable);
    }

    setVerified(): void {
        this.FwField.verified = Boolean(this.verified);
    }

    setMaxlength(): void {
        this.FwField.maxlength = +this.maxlength;
    }

    setErrors(): void {
        Vue.set(this.FwField, 'errors', this.errors || []);
    }

    setHint(): void {
        this.FwField.hint = this.hint;
    }

    onFocus(): void {
        this.FwField.focused = true;
        this.$emit('focus', this.localValue);
    }

    onBlur(): void {
        this.FwField.focused = false;
        this.$emit('blur', this.localValue);
    }

    clearField(): void {
        this.model = '';
        this.setFieldValue();
    }

    created() {
        this.model = this.localValue;
        this.setFieldValue();
        this.setPlaceholder();
        this.setName();
        this.setDisabled();
        this.setClearable();
        this.setMaxlength();
        this.setErrors();
        this.setVerified();
        this.setHint();
    }
}
